import { apiFetch } from "./client";
import type { AnnualView, OrganizerMatchRow, StandingsResponse, TopScorersResponse, TournamentTeamRow } from "./organizer";

export type PublicTournamentOverview = {
  tournament: {
    id: string;
    name: string;
    season: string;
    formatCode: string;
    status: string;
    leagueId: string;
    leagueName: string;
    /** Para dejar la dirección legible en la barra si se llegó por GUID. */
    leagueSlug?: string;
    tournamentSlug?: string | null;
    leagueLogoUrl: string | null;
    divisionName?: string | null;
    /** Competencia (102): el campeonato paralelo de la liga. */
    competitionName?: string | null;
    matchFormat?: string | null;
    periodCount?: number | null;
    periodMinutes?: number | null;
    isDoubleRound?: boolean;
  };
  champion: {
    teamId: string;
    teamName: string;
    logoUrl: string | null;
    reason: string;
  } | null;
};

// Los endpoints publicos NO requieren auth. Pasamos withAuth: false.
/**
 * Traduce el enlace legible /p/liga-del-oeste/apertura-2026 al id, que es lo
 * único que entienden los endpoints públicos. Se llama UNA vez al abrir.
 */
export async function resolverEnlacePublico(
  leagueSlug: string,
  tournamentSlug: string,
): Promise<{ tournamentId: string }> {
  // Le faltaban las dos cosas que tienen todas las de abajo: el prefijo
  // /api/v1 (sin él la URL es 404 y la página contesta "Ese torneo no existe
  // o cambió de dirección" para CUALQUIER enlace legible) y withAuth: false.
  return apiFetch(
    `/api/v1/public/r/${encodeURIComponent(leagueSlug)}/${encodeURIComponent(tournamentSlug)}`,
    { withAuth: false },
  );
}

export async function fetchPublicTournamentOverview(tournamentId: string): Promise<PublicTournamentOverview> {
  return apiFetch<PublicTournamentOverview>(
    `/api/v1/public/tournaments/${tournamentId}/overview`,
    { withAuth: false },
  );
}

/** Un equipo del buscador público (`/api/v1/public/teams?q=`). */
export type PublicTeam = {
  id: string;
  name: string;
  logoUrl: string | null;
  leagueId: string;
  leagueName: string;
};

/**
 * Buscar un equipo por su NOMBRE, sin saber la liga y SIN SESIÓN. El hincha
 * sabe "Barraca", no "Liga 3 Urban": el catálogo de ligas no le alcanza para
 * encontrarse, y pedirle una cuenta antes de dejarlo mirar es pedirle que se
 * case antes de la primera cita.
 *
 * A diferencia del catálogo de ligas, esto NO se puede filtrar en memoria —
 * son todos los equipos de todas las ligas públicas—, así que la búsqueda va
 * al server. El término viaja en la URL y el backend lo cachea 60 s.
 */
export async function searchPublicTeams(q: string): Promise<PublicTeam[]> {
  const termino = q.trim();
  if (termino.length < 2) return [];
  const res = await apiFetch<{ teams: PublicTeam[] }>(
    `/api/v1/public/teams?q=${encodeURIComponent(termino)}`,
    { withAuth: false },
  );
  return res.teams ?? [];
}

/** Lo que se ve de un equipo SIN cuenta: su torneo, su próximo y sus últimos. */
export type PublicTeamSummary = {
  team: {
    id: string;
    name: string;
    logoUrl: string | null;
    leagueId: string;
    leagueName: string;
    leagueLogoUrl: string | null;
  };
  tournament: {
    id: string;
    name: string | null;
    season: string | null;
    leagueSlug: string | null;
    tournamentSlug: string | null;
  } | null;
  nextMatch: OrganizerMatchRow | null;
  lastMatches: OrganizerMatchRow[];
};

export async function fetchPublicTeamSummary(teamId: string): Promise<PublicTeamSummary> {
  return apiFetch(`/api/v1/public/teams/${teamId}`, { withAuth: false });
}

export async function fetchPublicTeams(tournamentId: string): Promise<{ teams: TournamentTeamRow[] }> {
  return apiFetch(`/api/v1/public/tournaments/${tournamentId}/teams`, { withAuth: false });
}

export async function fetchPublicMatches(tournamentId: string): Promise<{ matches: OrganizerMatchRow[] }> {
  return apiFetch(`/api/v1/public/tournaments/${tournamentId}/matches`, { withAuth: false });
}

export async function fetchPublicStandings(tournamentId: string): Promise<StandingsResponse> {
  return apiFetch(`/api/v1/public/tournaments/${tournamentId}/standings`, { withAuth: false });
}

/**
 * Tabla anual del torneo (099). `annual: null` cuando este torneo no suma con
 * ningún otro — la página no muestra la solapa.
 */
export async function fetchPublicAnnual(tournamentId: string): Promise<{ annual: AnnualView | null }> {
  return apiFetch(`/api/v1/public/tournaments/${tournamentId}/annual`, { withAuth: false });
}

export async function fetchPublicTopScorers(tournamentId: string): Promise<TopScorersResponse> {
  return apiFetch(`/api/v1/public/tournaments/${tournamentId}/top-scorers`, { withAuth: false });
}
